import { LucideIcon } from '@kerfjs/ui/lucide-icon';
import { PopupMenu, type PopupMenuEntry } from '@kerfjs/ui/popup-menu';
import { type SafeHtml } from 'kerfjs';
import { Archive, ArchiveRestore, Download, Eye, History, RefreshCw } from 'lucide';

import type { BackgroundDocT } from '../../../data/api.js';
import { menuProps, type MenuTrigger } from '../../../lib/action-menu.js';

/** The selected Knowledge Base documents that are still listed, in list order. */
export function selectedLibraryDocuments(
  docs: readonly BackgroundDocT[],
  selectedIds: readonly string[],
): BackgroundDocT[] {
  return docs.filter((doc) => selectedIds.includes(doc.id));
}

/**
 * Actions for the selected Knowledge Base documents (HS2-KRH1MW), shared by the
 * tile context menu and the main toolbar's "…" menu like Demand › Documents:
 * open one, download the selection, a document's history, replace it with a new
 * version, and archive or unarchive the selection.
 */
export function libraryDocumentMenuEntries(selected: readonly BackgroundDocT[]): PopupMenuEntry[] {
  const one = selected.length === 1 ? selected[0] : undefined;
  const allArchived = selected.length > 0 && selected.every((doc) => doc.archived);
  return [
    {
      label: 'Open',
      icon: <LucideIcon icon={Eye} name="" />,
      action: 'library-doc-open-selected',
      disabled: one === undefined,
    },
    {
      label: selected.length === 1 ? 'Download' : `Download ${String(selected.length)} selected`,
      icon: <LucideIcon icon={Download} name="" />,
      action: 'library-docs-download',
      disabled: selected.length === 0,
    },
    { kind: 'divider' },
    {
      label: one !== undefined && one.versionNumber > 1 ? `History (${String(one.versionNumber)})` : 'History',
      icon: <LucideIcon icon={History} name="" />,
      action: 'library-doc-history',
      disabled: one === undefined,
    },
    {
      label: 'Replace with New Version',
      icon: <LucideIcon icon={RefreshCw} name="" />,
      action: 'library-doc-replace',
      disabled: one === undefined,
    },
    { kind: 'divider' },
    {
      label: allArchived ? 'Unarchive' : 'Archive',
      icon: <LucideIcon icon={allArchived ? ArchiveRestore : Archive} name="" />,
      action: allArchived ? 'library-docs-unarchive' : 'library-docs-archive',
      disabled: selected.length === 0,
    },
  ];
}

export function LibraryDocumentActionsMenu({
  selected,
  trigger,
}: {
  selected: readonly BackgroundDocT[];
  trigger: MenuTrigger;
}): SafeHtml {
  return <PopupMenu {...menuProps(trigger, libraryDocumentMenuEntries(selected))} />;
}
